import { useEffect, useMemo } from 'react';
import { InfiniteData, QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { plato_api_standards_path } from '@/modules/routes';
import { StandardNode, StandardsResponse } from '@/components/shared/Standards/types';
import useCorrelationsStore from '../useCorrelationsStore';
import { standardsQueryKey } from '../standardsFilterUtils';

export const PAGE_SIZE = 8;

interface BaseParams {
  enabled: boolean;
  grade: string | number | null;
  programId: number;
  stateId: number;
}

type ChildrenParams = BaseParams & { parentId: number };

export const fetchStandards = (params: Record<string, unknown>) => (
  axios
    .get<StandardsResponse>(plato_api_standards_path(), { params: { correlated_only: true, ...params } })
    .then(res => res.data)
);

type LevelParams = Omit<BaseParams, 'enabled'>;

const childrenKey = ({ grade, programId, stateId }: LevelParams, parentId: number) => (
  [...standardsQueryKey(stateId, programId), grade, 'children', parentId]
);

// Completes any partially fetched ("See more") child lists for the given parents, appending the
// remaining pages into the react-query cache. Returns every node fetched here so callers can use
// them synchronously. Unloaded levels are cold-loaded from page 1: search (PLAT-17644) lets the
// teacher check nodes whose intermediate levels were never expanded, and skipping them would
// silently drop their unfetched siblings from the selection.
export const ensureChildrenFullyLoaded = async (
  queryClient: QueryClient,
  params: LevelParams,
  parentIds: number[],
): Promise<StandardNode[]> => {
  const fetched: StandardNode[] = [];

  await Promise.all(parentIds.map(async (parentId) => {
    const key = childrenKey(params, parentId);
    let data = queryClient.getQueryData<InfiniteData<StandardsResponse>>(key);
    if (!data) {
      const first = await fetchStandards({
        grade: params.grade,
        page: 1,
        parent_tag_id: parentId,
        per_page: PAGE_SIZE,
        program_id: params.programId,
        state_id: params.stateId,
      });
      fetched.push(...first.data);
      data = { pageParams: [1], pages: [first] };
      queryClient.setQueryData(key, data);
    }

    let loaded = data.pages.reduce((sum, page) => sum + page.data.length, 0);
    const total = data.pages[0].meta.count;

    while (loaded < total) {
      // eslint-disable-next-line no-await-in-loop -- pages are sequential; each request needs the prior page count
      const page = await fetchStandards({
        grade: params.grade,
        page: data.pages.length + 1,
        parent_tag_id: parentId,
        per_page: PAGE_SIZE,
        program_id: params.programId,
        state_id: params.stateId,
      });
      fetched.push(...page.data);
      data = { pageParams: [...data.pageParams, data.pages.length + 1], pages: [...data.pages, page] };
      queryClient.setQueryData(key, data);
      loaded += page.data.length;
      if (page.data.length === 0) break;
    }
  }));

  return fetched;
};

const nextPage = (lastPage: StandardsResponse, allPages: StandardsResponse[]) => {
  const fetched = allPages.reduce((sum, page) => sum + page.data.length, 0);

  return fetched < lastPage.meta.count ? allPages.length + 1 : undefined;
};

// One level of the teacher's standards tree, pruned server-side to standards the program has
// correlations to, in batches of PAGE_SIZE that the "See more" control extends. Never fetch_all
// here — the filter panel loads lazily on expand.
const useStandardsLevel = (queryKey: unknown[], enabled: boolean, params: Record<string, unknown>) => {
  const mergeStandardsNodes = useCorrelationsStore(s => s.mergeStandardsNodes);
  const query = useInfiniteQuery({
    enabled,
    getNextPageParam: nextPage,
    queryFn: ({ pageParam = 1 }) => fetchStandards({ ...params, page: pageParam, per_page: PAGE_SIZE }),
    queryKey,
    staleTime: Infinity,
  });

  const nodes = useMemo<StandardNode[] | undefined>(
    () => query.data?.pages.flatMap(page => page.data),
    [query.data],
  );

  useEffect(() => {
    if (nodes) mergeStandardsNodes(nodes);
  }, [nodes, mergeStandardsNodes]);

  return {
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    isInitialLoading: query.isInitialLoading,
    nodes,
    totalCount: query.data?.pages[0]?.meta.count,
  };
};

export const useStandardsRoots = ({ enabled, grade, programId, stateId }: BaseParams) => useStandardsLevel(
  [...standardsQueryKey(stateId, programId), grade, 'roots'],
  enabled,
  { grade, program_id: programId, state_id: stateId },
);

export const useStandardsChildren = ({ enabled, grade, parentId, programId, stateId }: ChildrenParams) => (
  useStandardsLevel(
    [...standardsQueryKey(stateId, programId), grade, 'children', parentId],
    enabled,
    { grade, parent_tag_id: parentId, program_id: programId, state_id: stateId },
  )
);
